import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Layout } from "../../../../ui/components/Layout";
import { AuthTransitionScreen } from "../../../../ui/components/AuthTransitionScreen";
import { NavigationBar } from "../../../../ui/components/NavigationBar";
import type { NavOrgContext } from "../../../../ui/components/NavigationBar/NavigationBar.types";
import { DarkModeToggle } from "../../DarkModeToggle";
import { JoinOrganizationModal } from "../../profile/JoinOrganizationModal";
import { useAuth } from "../../../lib/auth/hooks";
import { useI18n } from "../../../lib/i18n";
import {
  buildNavigationBarProps,
  resolveActiveModuleSlug,
} from "./logic/lionProNavigation";
import { ACTIVE_MODULE_STORAGE_KEY } from "../../../lib/navigation/constants";
import {
  OrganizationContextProvider,
  useOrganizationContext,
} from "../../../lib/organization-context";
import { useIsLayoutMobile } from "../../../../ui/hooks/useLayoutMobile";
import { useFeatureFlagsBootstrap } from "../../../lib/featureFlags/hooks";
import { getAccountAppOrigin } from "../../../lib/auth/app-origins";
import { ProAccessGate } from "../../ProAccessGate";
import { RealtimeConnectionIndicator } from "../../RealtimeConnectionIndicator";
import * as s from "./AuthenticatedLayout.style.css";

function buildNavOrgContext(
  orgContext: ReturnType<typeof useOrganizationContext>,
  onJoinOrganizationClick: () => void,
  opts?: { hasProductAccess?: boolean },
): NavOrgContext {
  const selectAll = () =>
    orgContext.setSelectedOrgIds(orgContext.availableOrgs.map((o) => o.id));

  const soloOrg = (orgId: string) =>
    orgContext.setSelectedOrgIds([orgId]);

  // Product-only users: show Workspace/Products without forcing join-org mode.
  if (!orgContext.hasOrganizationMembership) {
    if (opts?.hasProductAccess) {
      return {
        mode: "single",
        selectedOrgs: [],
        availableOrgs: [],
        selectedOrgIds: [],
        onToggleOrg: () => {},
        onSoloOrg: () => {},
        onSelectAll: () => {},
      };
    }
    return {
      mode: "join",
      selectedOrgs: [],
      availableOrgs: [],
      selectedOrgIds: [],
      onToggleOrg: () => {},
      onSoloOrg: () => {},
      onSelectAll: () => {},
      onJoinOrganizationClick,
    };
  }

  if (orgContext.availableOrgs.length <= 1) {
    return {
      mode: "single",
      selectedOrgs: orgContext.selectedOrgs,
      availableOrgs: orgContext.availableOrgs,
      selectedOrgIds: orgContext.selectedOrgIds,
      onToggleOrg: orgContext.toggleOrg,
      onSoloOrg: soloOrg,
      onSelectAll: selectAll,
    };
  }

  return {
    mode: "multi",
    selectedOrgs: orgContext.selectedOrgs,
    availableOrgs: orgContext.availableOrgs,
    selectedOrgIds: orgContext.selectedOrgIds,
    onToggleOrg: orgContext.toggleOrg,
    onSoloOrg: soloOrg,
    onSelectAll: selectAll,
  };
}

function AuthenticatedLayoutInner({ children }: { children: React.ReactNode }) {
  const { user, logout, refreshUser } = useAuth();
  const { openLanguagePicker, t, currentLanguage } = useI18n();
  useFeatureFlagsBootstrap();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isLayoutMobile = useIsLayoutMobile();
  const orgContext = useOrganizationContext();
  const [joinOrgModalOpen, setJoinOrgModalOpen] = React.useState(false);

  const [activeModuleSlug, setActiveModuleSlug] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    if (!user?.navigation) return;
    const resolved = resolveActiveModuleSlug(
      user.navigation,
      pathname,
      activeModuleSlug,
    );
    if (resolved && resolved !== activeModuleSlug) {
      setActiveModuleSlug(resolved);
      try {
        sessionStorage.setItem(ACTIVE_MODULE_STORAGE_KEY, resolved);
      } catch {
        /* ignore */
      }
    }
  }, [user?.navigation, pathname, activeModuleSlug]);

  React.useEffect(() => {
    if (!user?.navigation.modules.length) return;
    if (activeModuleSlug) return;
    const initial = resolveActiveModuleSlug(user.navigation, pathname, null);
    if (initial) setActiveModuleSlug(initial);
  }, [user?.navigation, pathname, activeModuleSlug]);

  const handleLogout = React.useCallback(async () => {
    await logout();
    window.location.href = `${getAccountAppOrigin()}/login`;
  }, [logout]);

  const handleUnverifiedEmailClick = React.useCallback(() => {
    window.location.href = `${getAccountAppOrigin()}/user-setup?tab=email`;
  }, []);

  const handleJoinOrganizationClick = React.useCallback(() => {
    setJoinOrgModalOpen(true);
  }, []);

  if (!user) return null;

  const navProps = buildNavigationBarProps({
    user,
    activeModuleSlug,
    pathname,
    onModuleChange: setActiveModuleSlug,
    onNavigate: (to) => navigate({ to }),
    onLogout: handleLogout,
    onChangeLanguageClick: openLanguagePicker,
    onUnverifiedEmailClick: user.emailVerified ? undefined : handleUnverifiedEmailClick,
    orgContext: buildNavOrgContext(orgContext, handleJoinOrganizationClick, {
      hasProductAccess: Boolean(user.hasProductAccess),
    }),
    labels: {
      profile: t("shared.userMenu.profile"),
      changeLanguage: t("shared.userMenu.changeLanguage"),
      logOut: t("shared.userMenu.logOut"),
      languageName: currentLanguage?.name,
      languageFlagUrl: currentLanguage?.flagUrl,
    },
  });

  return (
    <>
      {!isLayoutMobile && <DarkModeToggle />}
      <Layout
        navigationBar={
          <NavigationBar
            {...navProps}
            headerTrailing={
              isLayoutMobile ? <DarkModeToggle placement="header" /> : undefined
            }
          />
        }
      >
        <div className={s.main}>
          <ProAccessGate>{children}</ProAccessGate>
        </div>
      </Layout>
      <JoinOrganizationModal
        open={joinOrgModalOpen}
        setOpen={setJoinOrgModalOpen}
        user={user}
        onDone={() => {
          void refreshUser();
        }}
      />
      <RealtimeConnectionIndicator />
    </>
  );
}

export function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();

  if (!user) {
    return isAuthenticated ? (
      <AuthTransitionScreen message="Loading your profile…" />
    ) : null;
  }

  return (
    <OrganizationContextProvider
      organizations={user.organizations}
      hasOrganizationMembership={user.hasOrganizationMembership}
    >
      <AuthenticatedLayoutInner>{children}</AuthenticatedLayoutInner>
    </OrganizationContextProvider>
  );
}
